import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useStudio } from '@/features/studio/contexts/StudioContext';
import { toast } from 'sonner';
import { Sentry } from '@/lib/sentry';

const ADMIN_SIM_KEY = 'admin_simulated_tier';

export function getAdminSimulatedTier(): TierKey {
  return (localStorage.getItem(ADMIN_SIM_KEY) as TierKey | null) ?? 'pro';
}

export function setAdminSimulatedTier(tier: TierKey) {
  localStorage.setItem(ADMIN_SIM_KEY, tier);
  window.dispatchEvent(new Event('admin-tier-change'));
}

export const TIERS = {
  free: {
    product_id: null as null,
    price_id_monthly: null as null,
    price_id_yearly:  null as null,
    name: { fr: 'Starter', en: 'Starter', es: 'Starter' },
    monthlyContracts: 20,
  },
  trial: {
    product_id: null as null,
    price_id_monthly: null as null,
    price_id_yearly:  null as null,
    name: { fr: 'Essai gratuit', en: 'Free trial', es: 'Prueba gratuita' },
    monthlyContracts: Infinity,
  },
  standard: {
    product_id: 'prod_UYNUaOJBcySvNq',
    price_id_monthly: 'price_1TZGjeCz8icEUXUDwOE3DaiK',
    price_id_yearly:  'price_1TZGkwCz8icEUXUDB6yw1oDF',
    name: { fr: 'Solo', en: 'Solo', es: 'Solo' },
    monthlyContracts: Infinity,
  },
  pro: {
    product_id: 'prod_UYNbSU6QGdbqk3',
    price_id_monthly: 'price_1TZGptCz8icEUXUDa7N9OdEF',
    price_id_yearly:  'price_1TZGqeCz8icEUXUDpmlxPRqy',
    name: { fr: 'Studio', en: 'Studio', es: 'Studio' },
    monthlyContracts: Infinity,
  },
  studio_pro: {
    product_id: 'prod_UYNe7Fnbj3FUH3',
    price_id_monthly: 'price_1TZGtaCz8icEUXUD4eSxe6Mv',
    price_id_yearly:  'price_1TZGtaCz8icEUXUDuUBtlYAt',
    name: { fr: 'Studio Pro', en: 'Studio Pro', es: 'Studio Pro' },
    monthlyContracts: Infinity,
  },
} as const;

export type TierKey = keyof typeof TIERS;

export function getTierByProductId(productId: string | null): TierKey {
  if (!productId) return 'free';
  for (const [key, tier] of Object.entries(TIERS)) {
    if (tier.product_id && tier.product_id === productId) return key as TierKey;
  }
  return 'free';
}

// Accounts created before this date are eligible for legacy free access
const LEGACY_CUTOFF_MS = new Date('2026-05-17T00:00:00Z').getTime();
// Grace period ends on this date — legacy users must subscribe after
const LEGACY_EXPIRY_MS = new Date('2026-07-01T00:00:00Z').getTime();

export function useSubscription() {
  const { session, isAdmin, loading: authLoading } = useAuth();
  const { membership } = useStudio();
  const isStudioMember = !!membership && !membership.isOwner && membership.status === 'active';
  const [tier, setTier] = useState<TierKey>('free');
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState<number>(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year' | null>(null);
  const [loading, setLoading] = useState(true);
  const hasShownError = useRef(false);
  const hasCheckedOnce = useRef(false);

  // Admin simulated tier — always declared, ignored for non-admin
  const [adminTier, setAdminTier] = useState<TierKey>(getAdminSimulatedTier);
  useEffect(() => {
    if (!isAdmin) return;
    const handler = () => setAdminTier(getAdminSimulatedTier());
    window.addEventListener('admin-tier-change', handler);
    return () => window.removeEventListener('admin-tier-change', handler);
  }, [isAdmin]);

  const checkSubscription = useCallback(async () => {
    if (isAdmin) { setLoading(false); return; }
    if (isStudioMember) { setTier('standard'); setLoading(false); return; }
    if (!session?.access_token) { if (!authLoading) { setTier('free'); setLoading(false); } return; }
    if (!hasCheckedOnce.current) setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      hasShownError.current = false;
      if (data?.subscribed) {
        const resolvedTier = getTierByProductId(data.product_id);
        setTier(resolvedTier);
        setSubscriptionEnd(data.subscription_end);
        setTrialEnd(data.trial_end ?? null);
        setSubscriptionStatus(data.subscription_status ?? null);
        setBillingInterval(data.billing_interval === 'year' ? 'year' : data.billing_interval === 'month' ? 'month' : null);
        setTrialDaysLeft(0);
      } else {
        const TRIAL_DURATION_DAYS = 14;
        const createdAt = session?.user?.created_at ? new Date(session.user.created_at).getTime() : 0; // Bug 1: 0 = epoch, trial expired long ago (fail-closed)
        const nowMs = Date.now();
        const trialEndMs = createdAt + (TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000);
        const isInTrialNow = nowMs < trialEndMs;
        const daysLeft = Math.max(0, Math.ceil((trialEndMs - nowMs) / (24 * 60 * 60 * 1000)));
        const legacy = createdAt < LEGACY_CUTOFF_MS && nowMs < LEGACY_EXPIRY_MS;

        // Bug 3: legacy checked first so it takes priority over trial
        if (legacy) {
          setTier('standard');
          setTrialDaysLeft(0);
        } else if (isInTrialNow) {
          setTier('trial');
          setTrialDaysLeft(daysLeft);
        } else {
          setTier('free');
          setTrialDaysLeft(0);
        }
        setSubscriptionEnd(null);
        setTrialEnd(null);
        setSubscriptionStatus(null);
        setBillingInterval(null);
      }
    } catch (e) {
      console.error('check-subscription error:', e);
      Sentry.captureException(e);
      if (!hasShownError.current && navigator.onLine) {
        hasShownError.current = true;
        toast.error('Impossible de vérifier votre abonnement. Votre plan actuel reste actif.');
      }
    } finally {
      hasCheckedOnce.current = true;
      setLoading(false);
    }
  }, [session?.access_token, isAdmin, isStudioMember, authLoading]);

  useEffect(() => {
    checkSubscription();
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [checkSubscription]);

  const startCheckout = async (priceId: string) => {
    if (isAdmin) return;
    const userId = session?.user?.id;
    if (!userId) throw new Error('User not authenticated');
    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: { priceId, userId },
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });
    if (error) throw error;
    if (data?.url) window.location.href = data.url;
  };

  const manageSubscription = async () => {
    if (isAdmin) return;
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (error) {
        const errorData = typeof error === 'object' && 'context' in error
          ? await (error as any).context?.json?.()
          : null;
        if (errorData?.error === 'no_customer') {
          toast.info('Vous n\'avez pas encore d\'abonnement actif. Choisissez un plan ci-dessous.');
          return;
        }
        throw error;
      }
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error('Erreur lors de l\'ouverture du portail');
      console.error('customer-portal error:', e);
    }
  };

  return {
    tier: isAdmin ? adminTier : isStudioMember ? ('standard' as TierKey) : tier,
    subscriptionEnd: isAdmin ? null : subscriptionEnd,
    trialEnd: isAdmin ? null : trialEnd,
    isLegacyFree: (() => {
      if (isAdmin || isStudioMember) return false;
      const createdAt = session?.user?.created_at
        ? new Date(session.user.created_at).getTime()
        : 0;
      return createdAt < LEGACY_CUTOFF_MS && Date.now() < LEGACY_EXPIRY_MS && tier === 'standard' && !subscriptionEnd;
    })(),
    loading: isAdmin || isStudioMember ? false : loading,
    trialDaysLeft: isAdmin || isStudioMember ? 0 : trialDaysLeft,
    isInTrial: !isAdmin && !isStudioMember && tier === 'trial',
    subscriptionStatus: isAdmin ? null : subscriptionStatus,
    billingInterval: isAdmin ? null : billingInterval,
    checkSubscription,
    startCheckout,
    manageSubscription,
  };
}
